import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
} from "react";
import type { ID, Rect, Region } from "@gooner/shared";
import { MIN_REGION, clampRect, snapRect } from "../lib/regionGeometry";
import { handleAtPoint } from "../lib/resizeHit";
import { syncTintMap } from "../lib/syncTints";
import { RegionBox, SURFACE_CLASS, surfaceStyle } from "./ViewSurface";
import type { HandleId, RegionMeta } from "./ViewSurface";

// RegionMeta is defined in ViewSurface (the box that renders it lives there) and
// re-exported here so canvas consumers keep one import site for canvas concerns.
export type { RegionMeta };

/** Convert client coords into normalized [0,1] coords inside `el`. Free-standing and
 *  pure so the long-lived drag handlers can call it without closing over a render. */
function toNorm(el: HTMLElement | null, clientX: number, clientY: number) {
  if (!el) return { x: 0, y: 0 };
  const r = el.getBoundingClientRect();
  return {
    x: Math.min(1, Math.max(0, (clientX - r.left) / r.width)),
    y: Math.min(1, Math.max(0, (clientY - r.top) / r.height)),
  };
}

interface DragState {
  kind: "move" | "resize";
  handle?: HandleId;
  /** pointer start, normalized */
  startX: number;
  startY: number;
  /** region rect at drag start */
  origin: Rect;
  regionId: ID;
}

/**
 * The region editor canvas. Renders normalized regions as absolutely positioned
 * boxes, supports select / move / resize / delete, and clamps coords. New regions
 * come from the Add Region tool and the layout presets — draw-to-create is gone,
 * and so is multi-select; one region is selected at a time.
 */
export function RegionCanvas({
  monitor,
  selectedId,
  onSelect,
  onChangeRect,
  onDeleteRegion,
  describeRegion,
  renderPreview,
  renderTileAction,
}: {
  /** Anything with an aspect and regions — a View or a Monitor. Named `monitor` for
   *  history; this canvas only ever reads its aspect and its region list. */
  monitor: { aspect: { w: number; h: number }; regions: Region[] };
  /** The selection — drawn with resize handles. */
  selectedId: ID | null;
  onSelect: (id: ID | null) => void;
  onChangeRect: (id: ID, rect: Rect) => void;
  onDeleteRegion: (id: ID) => void;
  /** Optional per-region caption (feed + timer). Omit for a bare canvas. */
  describeRegion?: (region: Region) => RegionMeta | null;
  /** Optional sample of what the region plays, drawn behind its caption. */
  renderPreview?: (region: Region) => ReactNode;
  /** Optional interactive control for the tile's top-right corner (the link button). */
  renderTileAction?: (region: Region) => ReactNode;
}) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);

  const syncTints = useMemo(() => syncTintMap(monitor.regions), [monitor.regions]);

  /** The callbacks as of the latest render, read at the moment a handler fires.
   *  Nothing registered on `window` may close over a prop — see `drag` below. */
  const liveRef = useRef({ onChangeRect });
  useLayoutEffect(() => {
    liveRef.current = { onChangeRect };
  });

  /**
   * Everything a drag does after `pointerdown`, built once on the first render and
   * never rebuilt, so `window` sees the same two function objects for the life of the
   * canvas: add and remove always match, and no re-render can unhook a gesture that is
   * in flight. That matters because a drag *always* re-renders mid-gesture — starting
   * one also moves the selection — and callers legitimately rebuild `onChangeRect`
   * on every render (any caller deriving it from the subject it is editing, which
   * is all of them). Handlers keyed on those props used to be torn down by their
   * own effect one tick after `beginDrag` installed them, and every drag and
   * resize silently did nothing. Correctness must not depend on callers memoizing.
   */
  const [drag] = useState(() => {
    const move = (e: PointerEvent) => {
      const ds = dragRef.current;
      if (!ds) return;
      const { x, y } = toNorm(surfaceRef.current, e.clientX, e.clientY);
      const dx = x - ds.startX;
      const dy = y - ds.startY;

      if (ds.kind === "move" && ds.regionId) {
        liveRef.current.onChangeRect(
          ds.regionId,
          snapRect({ ...ds.origin, x: ds.origin.x + dx, y: ds.origin.y + dy }),
        );
        return;
      }

      if (ds.kind === "resize" && ds.regionId && ds.handle) {
        liveRef.current.onChangeRect(
          ds.regionId,
          snapRect(resizeRect(ds.origin, ds.handle, dx, dy)),
        );
      }
    };

    // `pointercancel` ends a drag the same as `pointerup`. A touch gets cancelled — a
    // palm, a notification, the finger sliding off the glass — and a cancelled pointer
    // sends no `up`. Without this the drag stayed armed with its listeners on `window`,
    // and the region followed the next touch anywhere on the page.
    const end = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointercancel", end);
      window.removeEventListener("pointerup", end);
    };

    const begin = (ds: DragState) => {
      dragRef.current = ds;
      window.addEventListener("pointermove", move);
      window.addEventListener("pointercancel", end);
      window.addEventListener("pointerup", end);
    };

    return { begin, end, move };
  });

  // Unmount only: a drag left mid-flight by a disappearing canvas must not leave its
  // listeners on `window`. Deliberately empty deps — this effect re-running while a
  // drag is live is the exact bug described above.
  useEffect(() => {
    return () => {
      window.removeEventListener("pointermove", drag.move);
      window.removeEventListener("pointercancel", drag.end);
      window.removeEventListener("pointerup", drag.end);
    };
  }, [drag]);

  const beginDrag = drag.begin;

  const startMove = (e: ReactPointerEvent, region: Region) => {
    e.stopPropagation();
    onSelect(region.id);
    const p = toNorm(surfaceRef.current, e.clientX, e.clientY);
    beginDrag({
      kind: "move",
      startX: p.x,
      startY: p.y,
      origin: { x: region.x, y: region.y, w: region.w, h: region.h },
      regionId: region.id,
    });
  };

  const startResize = (
    e: ReactPointerEvent,
    region: Region,
    handle: HandleId,
  ) => {
    e.stopPropagation();
    onSelect(region.id);
    const p = toNorm(surfaceRef.current, e.clientX, e.clientY);
    beginDrag({
      kind: "resize",
      handle,
      startX: p.x,
      startY: p.y,
      origin: { x: region.x, y: region.y, w: region.w, h: region.h },
      regionId: region.id,
    });
  };

  /**
   * A finger grabs an edge of the SELECTED region from anywhere within a finger's width
   * of it — inside the box, outside it, or on a neighbour.
   *
   * The handles are 10px squares and every miss used to land on the box, whose own
   * pointerdown moves it: on a phone, widening a tile shoved it around instead. This
   * runs in the CAPTURE phase on the surface, so it sees the press before the box or a
   * handle does, and `startResize`'s stopPropagation keeps it from ever reaching them.
   * Where the press fell is pure geometry (lib/resizeHit): the selected region's box in
   * pixels against the pointer, with the band capped inside so a small tile keeps a
   * middle to be moved by.
   *
   * MOUSE IS EXEMPT, read off `pointerType` at pointerdown — the same fork the /screen
   * touch contract makes, and for the same reason: the two input models differ, and the
   * event says which one this is. A mouse has the handles, un-clipped now, and their
   * cursors say what a press will do; a band the cursor did not announce would make a
   * press near an edge resize when it looked like it would move.
   *
   * Only the selected region: the handles are drawn on it, so that is where a resize
   * is expected — and its edges winning over a flush neighbour is what "the handles are
   * there" means. An unselected tile is selected by a tap, as before.
   */
  const grabEdgeOnTouch = (e: ReactPointerEvent) => {
    if (e.pointerType === "mouse" || e.button !== 0) return;
    const region = monitor.regions.find((r) => r.id === selectedId);
    const surface = surfaceRef.current;
    if (!region || !surface) return;
    const s = surface.getBoundingClientRect();
    const handle = handleAtPoint(
      {
        left: s.left + region.x * s.width,
        top: s.top + region.y * s.height,
        width: region.w * s.width,
        height: region.h * s.height,
      },
      e.clientX,
      e.clientY,
    );
    if (handle) startResize(e, region, handle);
  };

  // Pointerdown on the empty surface (not a region) clears the selection. Drawing
  // a new region here is gone — Add Region and the layout presets create regions.
  const clearSelection = (e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    onSelect(null);
  };

  // Delete key removes the selected region while the canvas region is focused.
  const onKeyDown = (e: ReactKeyboardEvent) => {
    if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
      e.preventDefault();
      onDeleteRegion(selectedId);
    }
  };

  return (
    <div
      className="flex max-h-full w-full items-center justify-center outline-none"
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      <div
        ref={surfaceRef}
        onPointerDownCapture={grabEdgeOnTouch}
        onPointerDown={clearSelection}
        className={SURFACE_CLASS}
        style={surfaceStyle(monitor.aspect, {
          grid: true,
          maxHeight: "62vh",
        })}
      >
        {monitor.regions.map((region, i) => (
          <RegionBox
            key={region.id}
            region={region}
            index={i}
            selected={region.id === selectedId}
            meta={describeRegion?.(region) ?? null}
            preview={renderPreview?.(region) ?? null}
            syncTint={syncTints.get(region.id) ?? null}
            action={renderTileAction?.(region) ?? null}
            onPointerDown={(e) => startMove(e, region)}
            onResizeStart={(e, h) => startResize(e, region, h)}
          />
        ))}

        {monitor.regions.length === 0 ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="rounded-md bg-[var(--color-ink)]/70 px-3 py-1.5 text-xs text-zinc-500">
              Apply a layout, or use Add Region, to start
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Recompute a rect when dragging a given handle by (dx,dy) in normalized space. */
function resizeRect(
  origin: Rect,
  handle: HandleId,
  dx: number,
  dy: number,
): Rect {
  let { x, y, w, h } = origin;
  const right = origin.x + origin.w;
  const bottom = origin.y + origin.h;

  if (handle.includes("w")) {
    x = origin.x + dx;
    w = right - x;
  }
  if (handle.includes("e")) {
    w = origin.w + dx;
  }
  if (handle.includes("n")) {
    y = origin.y + dy;
    h = bottom - y;
  }
  if (handle.includes("s")) {
    h = origin.h + dy;
  }

  // Prevent inversion: if width/height collapse, pin the moving edge.
  if (w < MIN_REGION) {
    if (handle.includes("w")) x = right - MIN_REGION;
    w = MIN_REGION;
  }
  if (h < MIN_REGION) {
    if (handle.includes("n")) y = bottom - MIN_REGION;
    h = MIN_REGION;
  }

  return clampRect({ x, y, w, h });
}
